import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { equityCurve, EquityPoint } from "@/lib/aggregate";
import type { ScalpPosition } from "@/store/api";
import { fmtUsd } from "@/lib/format";

interface Props {
  positions: ScalpPosition[];
  height?: number;
  showAxis?: boolean;
}

export function EquityCurve({ positions, height = 200, showAxis = true }: Props) {
  const data = useMemo<EquityPoint[]>(() => equityCurve(positions), [positions]);
  if (data.length === 0) {
    return (
      <div
        className="grid place-items-center text-muted text-sm"
        style={{ height }}
      >
        No closed scalp trades yet — equity curve appears after the first close.
      </div>
    );
  }
  const final = data[data.length - 1].cumulative;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
            <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        {showAxis && (
          <XAxis
            dataKey="ts"
            stroke="hsl(var(--muted))"
            fontSize={11}
            tickFormatter={(t: string) => t.slice(5, 16).replace("T", " ")}
            minTickGap={32}
          />
        )}
        {showAxis && (
          <YAxis
            stroke="hsl(var(--muted))"
            fontSize={11}
            tickFormatter={(v: number) => `$${v}`}
            width={45}
          />
        )}
        <Tooltip
          contentStyle={{
            background: "hsl(var(--bg-2))",
            border: "1px solid hsl(var(--border))",
            borderRadius: 6,
            fontSize: 12,
          }}
          formatter={(v) => fmtUsd(typeof v === "number" ? v : Number(v))}
          labelFormatter={(l) => String(l).replace("T", " ").slice(0, 19)}
        />
        <ReferenceLine y={0} stroke="hsl(var(--muted))" strokeDasharray="3 3" />
        <Area
          type="monotone"
          dataKey="cumulative"
          stroke={final >= 0 ? "hsl(var(--good))" : "hsl(var(--bad))"}
          fill="url(#eq)"
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
